
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { mapGetters, mapState } from 'vuex';
import { GlobalState } from '@/shared/models/GlobalState';
import { required, maxLength, requiredIf, minLength, sameAs, email, integer } from '@vuelidate/validators';
import { adService } from './AdService';
import { accountService } from '../account/AccountService';
import store from '@/store';
import ResizableImage from '@/shared/components/ResizableImage.vue';
import SortableItem from '@/shared/components/SortableItem.vue';
import SortableList from '@/shared/components/SortableList.vue';
import MapPosition from '@/shared/components/MapPosition.vue';
import FlashMessage from '@/shared/components/FlashMessage.vue';
import FixedElement from '@/shared/components/FixedElement.vue';
import { Address } from '@/shared/models/Address';
import { Message, MessageType } from '@/shared/models/Message';
import { UploadFile } from '@/shared/models/UploadFile';
import { HandleDirective } from 'vue-slicksort';
import config from '@/shared/constants/config';
import { Point } from '@/shared/models/Point';
import AdPreview from './AdPreview.vue';
import { environment } from '@/environment/environment';
import useVuelidate from '@vuelidate/core';
import { scrollToAnchor } from '@/shared/helpers/scrollToAnchor';
import { googleTagManagerService } from '@/shared/services/GoogleTagManagerService';
import { estateAgencyService } from '@/estate-agency/EstateAgencyService';
import { countries, defaultCountry } from '@/shared/constants/mapCountries';
import Multiselect from 'vue-multiselect';
import Cropper from '@/shared/components/Cropper.vue';

export default defineComponent({
  mixins: [Form],
  setup() {
    return { v$: useVuelidate({ $scope: false }) };
  },
  components: {
    ResizableImage,
    SortableItem,
    SortableList,
    MapPosition,
    FlashMessage,
    AdPreview,
    FixedElement,
    Multiselect,
    Cropper,
  },
  props: {
    adFrontId: String,
    afterInsert: Boolean,
  },
  directives: { handle: HandleDirective },
  data() {
    return {
      bottomThreshold: 289,
      domain: config.DOMAIN,
      showPreview: false,
      isHeaderFixed: false,
      messages: [] as Message[],
      notificationClass: '',
      initialPosition: null as null | Point,
      isInserted: false,
      isPublished: false,
      isConfirmEmailSent: false,
      placeholderImagesCount: 3,
      logoUrl: config.BASE_URL + '/img/logo.svg',
      placeholderUrl: config.BASE_URL + '/img/upload_gallery_noimage.svg',
      defaultArea: 7,
      isUploading: false,
      filesToUpload: [] as string[] | Blob[],
      dragAndDropCapable: false,
      country: null,
      countries: null,
      publishStatuses: ['publish', 'archive'],
      publishStatus: 'publish',
      logoImage: {} as null | UploadFile,
      showLogoImageModal: false,
      form: {
        adId: 0,
        adFrontId: null as null | string,
        adGuid: '',
        street: '',
        streetNumber: '',
        city: '',
        countryCode: '',
        latitude: null as null | number,
        longitude: null as null | number,
        title: '',
        description: '',
        files: [] as UploadFile[],
        address: null as null | string,
        isPublished: false,
        isArchived: false,
        isEmailConfirmed: false,
        virtualTourLink: null as null | string,
        videoLink: null as null | string,
        maxFloor: 1 as null | number,
        hasWaterNetwork: null as null | boolean,
        hasGasNetwork: null as null | boolean,
        hasCanalizationNetwork: null as null | boolean,
        hasElectricityNetwork: null as null | boolean,
        logo: null as null | string,
      },
    };
  },
  beforeMount() {
    window.addEventListener('beforeunload', this.preventNav);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.preventNav);
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    const shouldAlert = this.isEditing;
    if (shouldAlert) {
      const answer = window.confirm(this.$t('page-leave-confirm'));
      if (!answer) {
        next(false);
        return;
      }
    }
    next();
  },
  validations() {
    const validations = {
      form: {
        address: { required },
        street: {},
        streetNumber: {},
        city: {},
        countryCode: { required },
        latitude: { required },
        longitude: { required },
        title: { required, maxLength: maxLength(70) },
        description: { required },
        virtualTourLink: {},
        videoLink: {},
        maxFloor: {
          required: requiredIf(() => {
            return this.form && this.form.floor ? this.form.floor > 0 : false;
          }),
          integer,
        },
        hasWaterNetwork: {},
        hasGasNetwork: {},
        hasCanalizationNetwork: {},
        hasElectricityNetwork: {},
      },
    };
    return validations;
  },
  watch: {
    windowWidth: function () {
      this.updateBottomThreshold();
    },
  },
  async created() {
    this.country = defaultCountry;
    this.countries = countries;
    this.updateBottomThreshold();
    this.isLoading = true;
    try {
      if (this.isEdit) {
        await this.loadData();
        scrollToAnchor(this.$route.hash);
      } else {
        const result = await adService.getNew();
        this.form.adGuid = result.data.model.adGuid;
        this.statuses = result.data.statuses;

        if (localStorage) {
          let lsForm = localStorage.getItem('adDeveloperForm');
          if (lsForm) {
            this.form = JSON.parse(lsForm);
            this.initSavedForm();
          }
        }
        this.isLoading = false;
      }
      this.handleFiles();
      this.handleFlashMessages();
    } catch (error) {
      console.error(error);
    }
  },
  mounted() {
    this.dragAndDropCapable = this.determineDragAndDropCapable();

    if (this.dragAndDropCapable) {
      ['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach((evt) => {
        (this.$refs.fileuploader as Element).addEventListener(
          evt,
          (e) => {
            e.preventDefault();
            e.stopPropagation();
          },
          false,
        );
      });
      (this.$refs.fileuploader as Element).addEventListener('drop', (e: any) => {
        for (let i = 0; i < e.dataTransfer.files.length; i++) {
          this.filesToUpload.push(e.dataTransfer.files[i]);
        }
        this.uploadFiles();
      });
    }
  },
  computed: {
    ...mapState({
      user: (state) => (state as GlobalState).user,
    }),
    ...mapGetters(['windowWidth', 'isMobile', 'isLoggedIn']),
    isEditing(): boolean {
      return this.v$.$anyDirty && this.v$.$invalid;
    },
    showFormMessages(): boolean {
      return this.isSuccess || this.isError || this.errors.length > 0;
    },
    isInsert(): boolean {
      return !this.adFrontId;
    },
    isEdit(): boolean {
      return !this.isInsert;
    },
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      const result = await adService.getById(this.adFrontId ? this.adFrontId : '');
      this.form = Object.assign({}, this.form, result.data.model);
      if (!result.data.model) {
        this.handleFlashMessages([new Message(this.$t('AdForm.not-found'), MessageType.Error)], true);
        return;
      }
      if (result.data.model.logo) {
        this.logoImage = {} as UploadFile;
        this.logoImage.file = result.data.model.logo;
        this.logoImage.url = result.data.model.logoRelativeUrl;
        this.logoImage.isSuccess = true;
      }
      this.form.hasEmptyPrice = !this.form.price;
      this.isPublished = this.form.isPublished;
      this.statuses = result.data.statuses;
      this.initSavedForm();
      this.isLoading = false;
    },
    updateBottomThreshold() {
      const footer = document.querySelector('footer');
      if (footer) {
        this.bottomThreshold = footer.clientHeight;
      }
    },
    preventNav(event: any) {
      if (!this.isEditing || environment !== 'production') {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
      if (localStorage && !this.isEdit) {
        const form = this.beforeSave();
        localStorage.setItem('adDeveloperForm', JSON.stringify(Object.assign({}, form, { password: null, confirmPassword: null })));
      }
    },
    initSavedForm() {
      if (this.form.countryCode) {
        let country = this.countries.find((x) => x.key === this.form.countryCode.toLowerCase());
        if (country) {
          this.country = country;
        }
      }

      if (this.form.latitude && this.form.longitude) {
        this.initialPosition = { lat: this.form.latitude, lng: this.form.longitude };
      }

      if (this.form.files) {
        for (const item of this.form.files) {
          item.isSuccess = true;
        }
      }
    },
    updateFixedStatus(isFixed: boolean) {
      this.isHeaderFixed = isFixed;
    },
    handleFlashMessages(appendMessages: Message[] = [], notFound = false) {
      const messages = [] as any[];
      if (!notFound) {
        if (!this.form.isPublished) {
          if (!this.form.isEmailConfirmed) {
            messages.push(new Message(this.$t('AdDeveloperProjectForm.saved-not-published-not-confirmed'), MessageType.Warning, 'confirmemail'));
          } else {
            messages.push(new Message(this.$t('AdDeveloperProjectForm.saved-not-published'), MessageType.Error));
          }
        }
      }
      if (appendMessages && appendMessages.length > 0) {
        messages.push(...appendMessages);
      }
      this.messages = messages;
      this.notificationClass = this.getNotificationClass();
    },
    getNotificationClass() {
      const errorExists = this.messages.some((x) => x.type === MessageType.Error);
      if (errorExists) return 'is-danger';
      const warningExists = this.messages.some((x) => x.type === MessageType.Warning);
      if (warningExists) return 'is-warning';
      return 'is-success';
    },
    onAddressChanged(address: Address) {
      if (address) {
        this.form.street = address.street;
        this.form.streetNumber = address.streetNumber;
        this.form.city = address.city;
        this.form.countryCode = address.countryCode;
        this.form.latitude = address.latitude;
        this.form.longitude = address.longitude;
        this.form.address = address.address;
      } else {
        this.form.street = '';
        this.form.streetNumber = '';
        this.form.city = '';
        this.form.countryCode = '';
        this.form.latitude = null;
        this.form.longitude = null;
        this.form.address = null;
      }

      if (this.form.countryCode) {
        let country = this.countries.find((x) => x.key === this.form.countryCode.toLowerCase());
        if (country) {
          this.country = country;
        }
      }

      const validationControl = (this.v$.form as any).address;
      if (validationControl) {
        validationControl.$touch();
      }
    },
    async sendConfirmEmail() {
      try {
        this.isLoading = true;
        await accountService.resetConfirmEmail();
        this.isConfirmEmailSent = true;
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    onPublishChanged() {
      this.publish(this.isPublished);
    },
    async onArchivedChanged() {
      try {
        this.isLoading = true;
        if (!this.form.isArchived) {
          await adService.archive(this.form.adId);
        } else {
          await adService.unarchive(this.form.adId);
        }
        await this.loadData();
        this.handleFlashMessages();
      } catch (error) {
        console.error(error);
        //this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    addPlaceholder() {
      this.form.files.push({
        isPlaceholder: true,
      } as UploadFile);
    },
    addFile(file: UploadFile) {
      this.form.files.push(file);
      this.handleFiles();
    },
    deleteFile(index: number) {
      this.form.files.splice(index, 1);
      this.handleFiles();
    },
    handleFiles() {
      if (!this.form.files) {
        this.form.files = [];
      }
      this.form.files = this.form.files.filter((x) => !x.isPlaceholder);
      for (let i = this.form.files.length; i < this.placeholderImagesCount; i++) {
        this.addPlaceholder();
      }
    },
    determineDragAndDropCapable() {
      const div = document.createElement('div');
      return ('draggable' in div || ('ondragstart' in div && 'ondrop' in div)) && 'FormData' in window && 'FileReader' in window;
    },
    async uploadFiles() {
      const files = this.filesToUpload;
      this.filesToUpload = [];
      if (files.length > 0) {
        this.isUploading = true;
        for (const file of files) {
          const uploadingFile: UploadFile = {} as UploadFile;
          uploadingFile.isUploading = true;
          uploadingFile.uploadPercentage = 0;
          uploadingFile.file = (file as any).name;
          this.addFile(uploadingFile);
          const formData = new FormData();
          formData.append('adGuid', this.form.adGuid);
          formData.append('file', file);
          try {
            const result = await adService.uploadImage(formData, (progressEvent: any) => {
              let file = this.form.files.find((f) => f.file === uploadingFile.file);
              if (file) {
                file.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString());
              }
            });
            uploadingFile.file = result.data.file;
            uploadingFile.url = result.data.url;
            uploadingFile.isSuccess = true;
          } catch (error) {
            uploadingFile.isError = true;
            uploadingFile.text = 'Error';
            const errors = this.getApiErrors(error);
            uploadingFile.text = errors[0].message;
          } finally {
            uploadingFile.isUploading = false;
          }
        }
        this.isUploading = false;
      }
    },
    async handleFilesUpload() {
      this.filesToUpload = (this.$refs.files as any).files as string[] | Blob[];
      await this.uploadFiles();
      (this.$refs.files as any).value = null;
    },
    async publish(value: boolean) {
      this.isInserted = false;
      try {
        this.isLoading = true;
        if (value) {
          await adService.publish(this.form.adId);
        } else {
          await adService.unpublish(this.form.adId);
        }
        this.form.isPublished = value;
        if (value) {
          this.handleFlashMessages([new Message(this.$t('AdDeveloperProjectForm.saved-published'), MessageType.Success)]);
        } else {
          this.handleFlashMessages();
        }
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    beforeSave() {
      const form = Object.assign({}, this.form);

      form.files = form.files.filter((x) => x.isSuccess);

      return form;
    },
    async save(redirect: boolean) {
      this.isInserted = false;

      if (!this.validate()) {
        return;
      }
      const form = this.beforeSave();

      try {
        this.isLoading = true;
        if (this.isInsert) {
          const result = await adService.insertDeveloperProject(form);
          if (localStorage) {
            localStorage.removeItem('adDeveloperForm');
          }

          this.logSuccess();
          this.$router.push({ name: 'AdDeveloperProjectFormDetail', params: { adFrontId: result.data.adFrontId } });
        } else {
          await adService.updateDeveloperProject(form);
          if (redirect) {
            this.$router.push({ name: 'AdDeveloperProjectList' });
          }
        }
        this.isSuccess = true;
        this.handleFlashMessages();
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },
    logSuccess() {
      let event = 'pridane_developersky_projekt';

      googleTagManagerService.send({
        event: event,
      });
    },
    async onShowPreview() {
      await this.save(false);
      this.showPreview = true;
    },
    subtractMaxFloor() {
      if (typeof this.form.maxFloor !== 'number') {
        this.form.maxFloor = 1;
      } else if (this.form.maxFloor > 1) {
        this.form.maxFloor = this.form.maxFloor - 1;
      }
    },
    addMaxFloor() {
      if (typeof this.form.maxFloor !== 'number') {
        this.form.maxFloor = 1;
      } else {
        this.form.maxFloor = this.form.maxFloor + 1;
      }
    },
    editLogoFile() {
      this.showLogoImageModal = true;
    },
    deleteLogoFile() {
      this.logoImage = {} as UploadFile;
      this.form.logo = null;
    },
    async handleLogoImageUpload() {
      const ref = this.$refs.logoImage as any;
      const files = ref.files as string[] | Blob[];
      const file = files[0];
      await this.uploadLogoImage(file);
      ref.value = null;
    },
    async uploadLogoImage(file: any) {
      this.showLogoImageModal = false;
      this.logoImage = {} as UploadFile;
      this.logoImage.isUploading = true;
      this.logoImage.uploadPercentage = 0;
      this.logoImage.file = file.name;
      const formData = new FormData();
      formData.append('file', file, file.name);

      try {
        const result = await adService.uploadDeveloperProjectLogo(formData, (progressEvent: any) => {
          if (this.logoImage) {
            this.logoImage.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString());
          }
        });
        this.logoImage.file = result.data.logo;
        this.logoImage.url = result.data.url;
        this.logoImage.isSuccess = true;
        this.form.logo = result.data.logo;
      } catch (error) {
        this.logoImage.isError = true;
        this.logoImage.text = 'Error';
        const errors = this.getApiErrors(error);
        this.logoImage.text = errors[0].message;
      } finally {
        this.logoImage.isUploading = false;
      }
    },
  },
});
